/**
 * 习惯行为指数观察窗参数化（P0-0.2）。
 *
 * V1 的窗口算在 `legacy/index.jsx#last21Range` 里，那份代码保持原样不动（D1：V1 链路零改动），
 * 这里把它抽成可传参的纯函数供 V2 使用：windowDays=21 时结果与 V1 逐字段相同，42 只在 V2 生效。
 *
 * ⚠️ 一个容易踩的语义：`dayKeys.length === windowDays` 是**请求窗口**，不是"采集了多少天"。
 * 窗口含当天（endOf('day')），当天数据天然不完整；真实观测天数只能取服务端的 `nDays`，
 * 数据截至日期只能取服务端的 `dataCutoff`（P0-0.3）。用 dayKeys.length 当分母就是 P6 锚点混用的重演。
 */
import moment from 'moment';

/** V1 行为：过去 21 天，保持不变 */
export const V1_WINDOW_DAYS = 21;
/** V2 观察窗（习惯养成语境，42 天） */
export const V2_WINDOW_DAYS = 42;
/** 上限：超过即视为配置错误，回落到默认窗口，避免前端发出跨年请求 */
export const MAX_WINDOW_DAYS = 366;

const DATE_KEY_FORMAT = 'YYYY-MM-DD';

/**
 * 归一化窗口天数：接受正整数（含数字字符串，便于 Apollo/URL 参数直接透传），
 * 非法值回落到 V1 的 21，绝不静默变成"任意值"。
 *
 * @param {number|string} windowDays 期望窗口天数
 * @returns {number} 合法窗口天数
 */
export function normalizeWindowDays(windowDays) {
  const numeric =
    typeof windowDays === 'string' && /^\d+$/.test(windowDays.trim())
      ? Number(windowDays.trim())
      : windowDays;
  if (!Number.isInteger(numeric)) return V1_WINDOW_DAYS;
  if (numeric < 1 || numeric > MAX_WINDOW_DAYS) return V1_WINDOW_DAYS;
  return numeric;
}

/**
 * 计算观察窗：起止日期 + 逐日对齐的日期键列表。
 *
 * 与 V1 完全一致的取整规则：起点 `subtract(n-1,'days').startOf('day')`，终点 `endOf('day')`，
 * 所以区间是闭区间且包含当天。
 *
 * @param {object} [options]
 * @param {number|string} [options.windowDays] 窗口天数，默认 21（V1 行为）
 * @param {Date|string|moment.Moment} [options.asOf] 基准时刻，默认当前时间；仅用于测试与"回看历史"
 * @param {boolean} [options.endAtYesterday] V2 专用：尾日退到**昨天**（当天数据天然不完整）
 * @param {boolean} [options.alignCompleteWeeks] V1/V2 旧口径：把窗口尾日退到最近一个已结束的周日
 * @returns {{windowDays: number, periodStart: string, periodEnd: string, dayKeys: string[], aligned: boolean, completeWeeks: number|null}}
 */
export function resolveHabitWindow({
  windowDays = V1_WINDOW_DAYS,
  asOf,
  endAtYesterday = false,
  alignCompleteWeeks = false,
} = {}) {
  const days = normalizeWindowDays(windowDays);
  const base = asOf === undefined || asOf === null ? moment() : moment(asOf);
  let end = base.clone().endOf('day');

  // V2 口径（2026-09-16 产品拍板）：**42 天截止到昨天**。
  // 旧口径（D15/决策 ⑨）把尾日退到"最近一个过完的周日"，代价是**本周已经发生的记录全落在窗外**
  // （实测：某会员 09-14 的睡眠在旧窗口里根本不存在 ⇒ 页面上"数据没了"，而 V1 看得见）。
  // 退一天就够：当天数据天然不完整，而昨天及以前都是完整的自然日。
  if (endAtYesterday) {
    end = end.subtract(1, 'day').endOf('day');
  }

  if (alignCompleteWeeks) {
    // 退到最近一个**已经过完**的周日。不退会同时坏两件事：
    // ① 本周没过完 ⇒ 周达成度的末槽永远被截断，6 个周槽最多用得上 5 个
    //    （`sport.stableAchiever` 这种以 6 为分母的判据就永远不可能命中）；
    // ② 当天的数据天然不完整，拿它当尾日就是 P6 锚点混用的重演。
    // isoWeekday()：周一=1…周日=7 ⇒ %7 把周日换成 0，正好是"距上一个周日还差几天"。
    end = end.subtract(end.isoWeekday() % 7, 'days').endOf('day');
  }
  const start = end
    .clone()
    .subtract(days - 1, 'days')
    .startOf('day');

  return {
    windowDays: days,
    periodStart: start.format(DATE_KEY_FORMAT),
    periodEnd: end.format(DATE_KEY_FORMAT),
    dayKeys: Array.from({ length: days }).map((_, i) =>
      start.clone().add(i, 'days').format(DATE_KEY_FORMAT),
    ),
    aligned: Boolean(alignCompleteWeeks),
    // 对齐后整窗刚好是这么多个自然周；没对齐时给 null而不是 0（"没这个口径"与"0 个周"是两件事）
    completeWeeks: alignCompleteWeeks ? Math.floor(days / 7) : null,
  };
}

/**
 * “上周”（V1 第一行的指数接口口径，**永久保留**）。
 *
 * 为什么不复用 resolveHabitWindow：这个接口的窗口是 V1 的老口径 —— moment 默认周起始
 * （周日）下的上一个完整自然周，跟 V2 的 42 天观察窗不是一个东西。
 * 2026-09-16 拍板：V1 第一行原样保留，所以这里也照原样取。
 *
 * @param {object} [options]
 * @param {Date|string|moment.Moment} [options.asOf] 基准时刻，默认当前时间
 * @returns {{periodStart: string, periodEnd: string}}
 */
export function resolveLastWeekWindow({ asOf } = {}) {
  const base = asOf === undefined || asOf === null ? moment() : moment(asOf);
  const lastWeek = base.clone().subtract(1, 'weeks');
  return {
    periodStart: lastWeek.clone().startOf('week').format(DATE_KEY_FORMAT),
    periodEnd: lastWeek.clone().endOf('week').format(DATE_KEY_FORMAT),
  };
}

/**
 * 把服务端日粒度序列（`{ key, value, observed }`）按窗口补齐/对齐。
 * 服务端已保证逐日等长，这里只兜两种意外：服务端少给日（旧包或未覆盖窗口）、以及顺序错乱。
 * 缺失项一律按 `observed:null` 返回——是"服务端没说"，不是"用户没做"，两者不可混为一谈。
 *
 * @param {Array<{key?: string}>} items 服务端序列
 * @param {string[]} dayKeys 窗口日期键
 * @returns {Array<object|null>} 与 dayKeys 等长、按位对齐的项数组，缺位为 null
 */
export function alignSeriesToWindow(items, dayKeys = []) {
  const byKey = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item && typeof item.key === 'string' && !byKey.has(item.key)) {
      byKey.set(item.key, item);
    }
  });
  return dayKeys.map((key) => byKey.get(key) || null);
}
